"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BackdropObserverHelper = void 0;
exports.getGlobalBackdropObserver = getGlobalBackdropObserver;
const attribute_observer_helper_1 = require("./attribute-observer.helper");
/**
 * BackdropObserverHelper - A static utility to observe body class changes for backdrop detection
 *
 * This helper provides a clean way to watch for the 'has-backdrop' class on the document body
 * and execute callbacks when the state changes.
 */
class BackdropObserverHelper {
    /**
     * Get the current backdrop state
     */
    static get hasBackdrop() {
        var _a, _b;
        if (!this.instance) {
            this.initialize();
        }
        return (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.hasClass('has-backdrop')) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * Initialize the observer if not already done
     */
    static initialize(shadowRoot) {
        if (this.isInitialized)
            return;
        this.instance = new attribute_observer_helper_1.BodyClassObserverHelper(shadowRoot);
        // Subscribe to class changes and notify our subscribers
        this.instance.subscribeToClass('has-backdrop', (hasBackdrop) => {
            this.notifySubscribers(hasBackdrop);
        });
        this.instance.start();
        this.isInitialized = true;
    }
    /**
     * Notify all subscribers of backdrop state change
     */
    static notifySubscribers(hasBackdrop) {
        this.subscribers.forEach(callback => {
            try {
                callback(hasBackdrop);
            }
            catch (error) {
                console.error('Error in backdrop observer callback:', error);
            }
        });
    }
    /**
     * Subscribe to backdrop state changes
     * @param callback Function to call when backdrop state changes
     * @param shadowRoot Optional shadow root for document context (only used on first call)
     * @returns Unique subscriber ID for unsubscribing
     */
    static subscribe(callback, shadowRoot) {
        if (!this.isInitialized) {
            this.initialize(shadowRoot);
        }
        const subscriberId = Symbol('backdrop-subscriber');
        this.subscribers.set(subscriberId, callback);
        // Immediately call with current state
        callback(this.hasBackdrop);
        return subscriberId;
    }
    /**
     * Unsubscribe from backdrop state changes
     * @param subscriberId The ID returned from subscribe()
     */
    static unsubscribe(subscriberId) {
        this.subscribers.delete(subscriberId);
        // If no more subscribers, stop the observer
        if (this.subscribers.size === 0) {
            this.stop();
        }
    }
    /**
     * Stop the observer and clean up resources
     */
    static stop() {
        if (this.instance) {
            this.instance.stop();
            this.instance = null;
        }
        this.subscribers.clear();
        this.isInitialized = false;
    }
    /**
     * Restart the observer (useful if document context changes)
     */
    static restart(shadowRoot) {
        this.stop();
        this.initialize(shadowRoot);
    }
    /**
     * Get the number of active subscribers
     */
    static get subscriberCount() {
        return this.subscribers.size;
    }
    /**
     * Check if the observer is currently running
     */
    static get isRunning() {
        return this.isInitialized && this.instance !== null;
    }
}
exports.BackdropObserverHelper = BackdropObserverHelper;
BackdropObserverHelper.instance = null;
BackdropObserverHelper.subscribers = new Map();
BackdropObserverHelper.isInitialized = false;
/**
 * Get or create a global backdrop observer instance
 * @deprecated Use BackdropObserverHelper.subscribe() instead
 */
function getGlobalBackdropObserver() {
    console.warn('getGlobalBackdropObserver is deprecated. Use BackdropObserverHelper directly.');
    return BackdropObserverHelper;
}
